import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { dateAsString, dayAsString } from '../../shared/date.helper';
import { Inspection } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';

@Component({
  selector: 'app-inspection-accepted',
  templateUrl: './inspection-accepted.component.html',
  styleUrls: ['./inspection-accepted.component.scss'],
})
export class InspectionAcceptedComponent implements OnInit {
  inspections: Inspection[] = null;

  page = 1;
  pageSize = 10;
  count = 0;
  search = '';
  url = 'inspection';
  column = [
    { field: 'customerName', header: 'Customer', type: 'text', orderBy: 'customer.name' },
    { field: 'address', header: 'Address', type: 'text', orderBy: 'location.address' },
    { field: 'city', header: 'City', type: 'text', orderBy: 'location.city' },
    { field: 'state', header: 'State', type: 'text', orderBy: 'location.state' },
    { field: 'purpose', header: 'Purpose', type: 'text', orderBy: 'inspection.purpose' },
    { field: 'policyNumber', header: 'Policy No.', type: 'text', orderBy: 'policy.number' },
    { field: 'vendor.name', header: 'Vendor', type: 'text', orderBy: 'vendor.firstName' },
    { field: 'inspector.name', header: 'Inspector', type: 'text', orderBy: 'inspector.firstName' },
    { field: 'rating', header: 'Rating', type: 'text', orderBy: 'inspection.rating' },
    { field: 'completed', header: 'Completed', type: 'date', orderBy: 'inspection.completed' },
    { field: 'lastSynced', header: 'Last Synced', type: 'date', orderBy: 'inspection.lastSynced' },
    { field: 'flaggingScore', header: 'Score', type: 'text', orderBy: 'inspection.flaggingScore' },
    { field: 'accepted', header: 'Accepted', type: 'date', orderBy: 'inspection.accepted' },
    { field: 'paid', header: 'Paid', type: 'date', orderBy: 'inspection.paid' },
    { header: 'Download Report', type: 'downloadReport' },
  ];

  actions = {
    list: [
      {
        label: 'Review',
        callback: (Inspection: Inspection) => async () => {
          console.log('Review', Inspection.id);
          return this.Review(Inspection.id);
        },
        show: item => () => {
          return this.canReview;
        },
      },
    ],
  };

  canAdd = false;
  canReview = false;
  loading = true;
  isVendor = false;

  loadDebounced = new Subject();

  dateAsString = dateAsString;

  constructor(
    public inspectionService: InspectionService,
    public userService: UserService,
    private toastr: ToastrService,
    private router: Router,
    private dialog: DialogService,
  ) {}

  async ngOnInit() {
    // Debounce data loading
    this.loadDebounced
      .pipe(
        debounceTime(500),
        switchMap(params => this.loadDataLive(params)),
      )
      .subscribe();

    this.canAdd = await this.userService.hasPermissions([permissions.INSPECTION.CREATE]);
    this.canReview = await this.userService.hasPermissions([permissions.INSPECTION.REVIEW]);
    this.isVendor = (await this.userService.profile()).type === 'Vendor';

    if (this.isVendor) {
      this.column = this.column.filter(c => c.field !== 'vendor.name');
      this.url = 'inspection/vendor';
    }
  }

  loadData(params) {
    this.loadDebounced.next(params);
  }

  async loadDataLive(params) {
    let rows: any[];

    [rows, this.count] = await this.inspectionService.list({ ...params, ...{ status: InspectionStatus.ACCEPTED } });

    this.inspections = rows.map(i => ({
      ...i,
      day: i.dueDate ? dayAsString(new Date(i.dueDate)) : '',
    }));
    this.loading = false;
  }

  Review(id: number) {
    this.router.navigateByUrl(`/inspection/review/main/${id}`);
  }

  async createReport(inspection: Inspection) {
    let sendReport = true;
    const currentUser = await this.userService.profile();
    const currentUserId = currentUser?.id;
    await this.inspectionService.listReports(inspection.id).then(list => {
      const running = list.find(
        r => !r.completed && r.requestedByUserId != null && r.requestedByUserId === currentUserId,
      );

      if (running) {
        sendReport = false;
        this.dialog.alert().show({
          header: `Report generation is in progress`,
          message: `A report for this inspection is already being generated. You’ll receive a download link via email once it’s ready.`,
        });
      }
    });
    if (sendReport) {
      this.dialog.confirm().show({
        header: 'Create inspection report',
        message: `The report will be generated and you'll receive a download link in your e-mail when it's ready`,
        confirmed: async () => {
          this.toastr.success('Your report is being generated');
          const reportParams = {
            applyFilters: false,
            showAdminComments: false,
            showFlags: false,
            showInspectorComments: true,
            showLegacyIds: false,
            showMarkupOverPictures: false,
            showNonMandatory: true,
            showQuestions: true,
          };
          await this.inspectionService.generateReport(inspection.id, {
            ...reportParams,
            filters: undefined,
          });
        },
      });
    }
  }
}
